import apiClient from "./apiClient";

export const boxerService = {

  // ── Delivery Command Center ───────────────────────────────────
  async getDeliveryCommandCenter() {
    const r = await apiClient.get("/api/boxer/delivery-command-center");
    return r.data;
  },
  async getCCSnapshot() {
    const r = await apiClient.get("/api/boxer/delivery-command-center/snapshot");
    return r.data;
  },
  async getCCAiInsight() {
    const r = await apiClient.get("/api/boxer/delivery-command-center/ai-insight");
    return r.data;
  },
  async getCCLiveNetwork() {
    const r = await apiClient.get("/api/boxer/delivery-command-center/live-network");
    return r.data;
  },
  async getCCUnshipped() {
    const r = await apiClient.get("/api/boxer/delivery-command-center/unshipped");
    return r.data;
  },
  async getCCAtRisk() {
    const r = await apiClient.get("/api/boxer/delivery-command-center/at-risk");
    return r.data;
  },

  // ── Delivery Monitoring ───────────────────────────────────────
  async getDeliveryMonitoring(fromDate = null, toDate = null) {
    const params = {};
    if (fromDate) params.from_date = fromDate;
    if (toDate)   params.to_date   = toDate;
    const r = await apiClient.get("/api/boxer/delivery-monitoring", { params });
    return r.data;
  },
  async getMonKpis(fromDate = null, toDate = null) {
    const params = {};
    if (fromDate) params.from_date = fromDate;
    if (toDate)   params.to_date   = toDate;
    const r = await apiClient.get("/api/boxer/delivery-monitoring/kpis", { params });
    return r.data;
  },
  async getMonNetworkWide(fromDate = null, toDate = null) {
    const params = {};
    if (fromDate) params.from_date = fromDate;
    if (toDate)   params.to_date   = toDate;
    const r = await apiClient.get("/api/boxer/delivery-monitoring/network-wide", { params });
    return r.data;
  },
  async getMonDcPerformance(fromDate = null, toDate = null) {
    const params = {};
    if (fromDate) params.from_date = fromDate;
    if (toDate)   params.to_date   = toDate;
    const r = await apiClient.get("/api/boxer/delivery-monitoring/dc-performance", { params });
    return r.data;
  },
  async getMonStorePerformance(fromDate = null, toDate = null) {
    const params = {};
    if (fromDate) params.from_date = fromDate;
    if (toDate)   params.to_date   = toDate;
    const r = await apiClient.get("/api/boxer/delivery-monitoring/store-performance", { params });
    return r.data;
  },
  async getMonCriticalLane(fromDate = null, toDate = null) {
    const params = {};
    if (fromDate) params.from_date = fromDate;
    if (toDate)   params.to_date   = toDate;
    const r = await apiClient.get("/api/boxer/delivery-monitoring/critical-lane", { params });
    return r.data;
  },

  // ── Chat: non-streaming fallback ──────────────────────────────
  async sendChatMessage(message, history = [], queryMode = "schema", modelKey = "claude_sonnet") {
    const response = await apiClient.post("/api/boxer/chat", {
      message,
      history,
      query_mode: queryMode,
      model_key:  modelKey,
    });
    return response.data;
  },

  // ── Chat: streaming (SSE) ─────────────────────────────────────
  // Reads newline-delimited JSON events from /api/boxer/chat/stream
  // Calls onEvent(event) for each parsed event object.
  //
  // Event types:
  //   { event: "table",   table_data, table_columns, row_count, ... }
  //   { event: "token",   token: "word " }
  //   { event: "done",    suggestions: [...] }
  //   { event: "error",   message: "..." }
  async streamChatMessage(message, history = [], queryMode = "schema", modelKey = "claude_sonnet", onEvent, signal) {
    // Get auth token — adjust key name to match your auth implementation
    const token = localStorage.getItem("token")
      || localStorage.getItem("access_token")
      || localStorage.getItem("authToken")
      || "";

    const baseURL = apiClient.defaults.baseURL || "";

    const response = await fetch(`${baseURL}/api/boxer/chat/stream`, {
      method:  "POST",
      headers: {
        "Content-Type":  "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({
        message,
        history,
        query_mode: queryMode,
        model_key:  modelKey,
      }),
      signal,
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(errText || `HTTP ${response.status}`);
    }

    const reader  = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer    = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop(); // keep last incomplete line

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        try {
          const event = JSON.parse(trimmed);
          onEvent(event);
        } catch {
          // incomplete JSON line — wait for more data
        }
      }
    }

    // Process any remaining buffer
    if (buffer.trim()) {
      try {
        const event = JSON.parse(buffer.trim());
        onEvent(event);
      } catch {
        // ignore
      }
    }
  },

  // ── Suggestions (separate call, non-blocking) ─────────────────
  async fetchSuggestions(message, history = [], modelKey = "claude_sonnet") {
    const response = await apiClient.post("/api/boxer/chat/suggestions", {
      message,
      history,
      model_key: modelKey,
    });
    return response.data;
  },

  // ── Chat Sessions ─────────────────────────────────────────────
  _authHeader() {
    return { Authorization: `Bearer ${localStorage.getItem("access_token")}` };
  },
  async createSession(title, queryMode) {
    const r = await apiClient.post("/api/boxer/chat/sessions",
      { title, query_mode: queryMode },
      { headers: this._authHeader() }
    );
    return r.data.session_id;
  },
  async getSessions() {
    const r = await apiClient.get("/api/boxer/chat/sessions", { headers: this._authHeader() });
    return r.data.sessions || [];
  },
  async getSession(sessionId) {
    const r = await apiClient.get(`/api/boxer/chat/sessions/${sessionId}`, { headers: this._authHeader() });
    return r.data;
  },
  async saveMessages(sessionId, messages) {
    await apiClient.patch(`/api/boxer/chat/sessions/${sessionId}`,
      { messages },
      { headers: this._authHeader() }
    );
  },
  async renameSession(sessionId, title) {
    await apiClient.patch(`/api/boxer/chat/sessions/${sessionId}/rename`,
      { title },
      { headers: this._authHeader() }
    );
  },
  async pinSession(sessionId, pinned) {
    await apiClient.patch(`/api/boxer/chat/sessions/${sessionId}/pin`,
      { pinned },
      { headers: this._authHeader() }
    );
  },
  async deleteSession(sessionId) {
    await apiClient.delete(`/api/boxer/chat/sessions/${sessionId}`, { headers: this._authHeader() });
  },
  async submitFeedback(data) {
    const r = await apiClient.post("/api/boxer/chat/feedback", data, { headers: this._authHeader() });
    return r.data;
  },

  // ── Distill conversation ──────────────────────────────────────
  async distillConversation(history) {
    const r = await apiClient.post(
      "/api/boxer/chat/distill",
      { history },
      { headers: this._authHeader() }
    );
    return r.data.prompt;
  },

  // ── Saved Prompts ─────────────────────────────────────────────
  async getSavedPrompts() {
    const r = await apiClient.get("/api/boxer/saved-prompts", { headers: this._authHeader() });
    return r.data.prompts || [];
  },
  async createSavedPrompt(title, content, queryMode) {
    const r = await apiClient.post(
      "/api/boxer/saved-prompts",
      { title, content, query_mode: queryMode },
      { headers: this._authHeader() }
    );
    return r.data;
  },
  async deleteSavedPrompt(promptId) {
    await apiClient.delete(`/api/boxer/saved-prompts/${promptId}`, { headers: this._authHeader() });
  },

  async clearAllSessions() {
    await apiClient.delete("/api/boxer/chat/sessions/all", { headers: this._authHeader() });
  },
  async clearAllPrompts() {
    await apiClient.delete("/api/boxer/saved-prompts/all", { headers: this._authHeader() });
  },

  // ── User Preferences ─────────────────────────────────────────
  async getPreferences() {
    const r = await apiClient.get("/api/boxer/chat/preferences", { headers: this._authHeader() });
    return r.data;
  },
  async savePreferences(opslense, docusense, focus) {
    const r = await apiClient.patch("/api/boxer/chat/preferences",
      { opslense, docusense, focus },
      { headers: this._authHeader() }
    );
    return r.data;
  },

  // ── User Memory ───────────────────────────────────────────────
  async getMyMemory() {
    const r = await apiClient.get("/api/boxer/chat/memory", { headers: this._authHeader() });
    return r.data;
  },
  async clearMemory() {
    await apiClient.delete("/api/boxer/chat/memory", { headers: this._authHeader() });
  },
};